// TIP-493 (MV Stage 4b) — the persistent right-hand order summary shown on every
// step of the Buy-a-Web-Profile checkout (plan select, payment, success).
// TIP-646 — renders two states: ex-VAT before a billing country is known, and
// Stripe's real quoted tax once the billing-address step has one.
import React from "react";
import { WEB_PROFILE_MEMBER } from "../../../constants/webProfilePricing";
import { Aed } from "../../common/Money";

const initialsOf = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "—";

export const WebProfileSummary = ({
  company,
  summary,
  upsell = false,
  onSwitchToMembership,
  fallbackName, // TIP-556 — report orders have no company row yet; show the requested name
}) => {
  const companyName =
    company?.registered_company_name || company?.company_name || fallbackName || "this business";
  // TIP-646 — `quoted` is set by applyQuotedTax once create_payment has
  // returned Stripe's automatic_tax figures for the selected billing address.
  const quoted = Boolean(summary?.quoted);
  const companyId = company?.companyID || "";
  const code = (company?.country_code || company?.country || "").toUpperCase();
  // 🇦🇪-style flag from the 2-letter code + full country name (no extra deps).
  const flag =
    code.length === 2
      ? String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
      : "";
  let countryName = code;
  try {
    countryName = new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch (e) {
    /* older browsers — fall back to the code */
  }

  return (
    <div className="w-full sm:w-[340px] shrink-0">
      <div className="bg-white rounded-md p-6 border border-gray-200">
        <p className="text-secondary text-f12 mb-3">You're buying claim to</p>

        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="w-10 h-10 rounded-md bg-button text-white flex items-center justify-center text-f14 font-semibold shrink-0">
            {initialsOf(companyName)}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-primaryText text-f14 truncate">
              {companyName}
            </p>
            <p className="text-secondary text-f12 truncate">
              {flag && <span className="mr-1">{flag}</span>}
              {countryName}
              {countryName && companyId ? " · " : ""}
              {companyId}
            </p>
          </div>
        </div>

        <div className="py-4 space-y-2">
          {summary?.lines?.map((line) => (
            <div key={line.key} className="flex justify-between items-baseline text-f14">
              <span className={line.savings ? "text-[#1A8917]" : "text-primaryText"}>
                {line.label}
                {line.sublabel && (
                  <span className="block text-secondary text-f12">{line.sublabel}</span>
                )}
              </span>
              <span
                className={`inline-flex items-center gap-2 ${
                  line.savings ? "text-[#1A8917]" : "text-primaryText"
                }`}
              >
                {line.strike && (
                  <span className="text-secondary line-through">
                    <Aed value={line.strike} />
                  </span>
                )}
                <Aed value={line.value} />
              </span>
            </div>
          ))}

          {/* TIP-646 — VAT is Stripe's to compute from the billing country, so
              it is only stated once a quote exists. Before that (the plan-select
              step) the widget says VAT is excluded rather than naming a rate:
              the old flat "VAT (5%)" row quoted UAE tax to buyers outside the
              UAE, who are charged none. Mirrors the report-order sidebar in
              CreateCompany.jsx. */}
          <div className="flex justify-between items-baseline text-f14 text-secondary">
            <span>VAT</span>
            {quoted ? (
              <span><Aed value={summary.vat ?? 0} /></span>
            ) : (
              <span>excluded</span>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-gray-100">
          <span className="font-semibold text-primaryText">
            {quoted ? "Total" : "Total (excl. VAT)"}
          </span>
          <span className="font-bold text-primaryText text-f18">
            <Aed value={summary?.total ?? 0} />
          </span>
        </div>

        {!quoted && (
          <p className="text-secondary text-f12 mt-1">
            VAT is added at the secure checkout, based on your billing country.
          </p>
        )}

        {summary?.renewalNote && (
          <p className="text-secondary text-f12 mt-3">{summary.renewalNote}</p>
        )}
      </div>

      {upsell && (
        <div className="bg-button text-white rounded-md p-5 mt-4">
          <p className="font-semibold text-f14 mb-1">Buying more than one this year?</p>
          <p className="text-f12 text-[#B0C5D1] mb-4 inline-flex flex-wrap items-center gap-1">
            Members pay <Aed value={WEB_PROFILE_MEMBER} /> on every Web Profile, plus
            Payment Endorsements, the Partner Program, and the path to Verified Member.
          </p>
          <button
            type="button"
            onClick={onSwitchToMembership}
            className="border border-[rgba(255,255,255,0.3)] text-white text-f14 rounded-md px-4 py-2 hover:bg-[rgba(255,255,255,0.08)]"
          >
            Switch to Membership
          </button>
        </div>
      )}
    </div>
  );
};

export default WebProfileSummary;
